import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.scss';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
export const Modal = ({ isOpen, onClose, title, children, className, labelledBy, }) => {
    const generatedTitleId = useId();
    const titleId = labelledBy || (title ? generatedTitleId : undefined);
    const contentRef = useRef(null);
    const previouslyFocusedRef = useRef(null);
    useEffect(() => {
        if (!isOpen)
            return undefined;
        previouslyFocusedRef.current = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const focusFirstElement = () => {
            const node = contentRef.current;
            if (!node)
                return;
            const focusable = node.querySelector(FOCUSABLE_SELECTOR);
            (focusable || node).focus();
        };
        focusFirstElement();
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose();
                return;
            }
            if (event.key === 'Tab') {
                const node = contentRef.current;
                if (!node)
                    return;
                const focusableElements = Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR));
                if (focusableElements.length === 0) {
                    event.preventDefault();
                    return;
                }
                const first = focusableElements[0];
                const last = focusableElements[focusableElements.length - 1];
                const active = document.activeElement;
                if (event.shiftKey) {
                    if (active === first || !node.contains(active)) {
                        event.preventDefault();
                        last.focus();
                    }
                }
                else if (active === last || !node.contains(active)) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = previousOverflow;
            previouslyFocusedRef.current?.focus?.();
        };
    }, [isOpen, onClose]);
    if (!isOpen)
        return null;
    const handleOverlayClick = () => {
        onClose();
    };
    const handleContentClick = (event) => {
        event.stopPropagation();
    };
    const portalTarget = document.getElementById('modal-root') || document.body;
    return createPortal(_jsx("div", { className: styles.overlay, onClick: handleOverlayClick, children: _jsxs("div", { className: [styles.content, className].filter(Boolean).join(' '), role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, ref: contentRef, tabIndex: -1, onClick: handleContentClick, children: [title && (_jsx("h2", { id: titleId, className: styles.title, children: title })), children, _jsx("button", { type: "button", className: styles.closeButton, "aria-label": "Close", onClick: onClose, children: "\u00D7" })] }) }), portalTarget);
};
